import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  Activity, 
  AlertTriangle, 
  Eye, 
  MousePointer, 
  TrendingUp,
  Clock,
  AlertCircle,
  Image,
  Moon,
  Sun,
  Settings
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import AdminSidebar from './AdminSidebar';
import AdminBookings from './AdminBookings';
import AdminCMS from './AdminCMS';
import AdminServices from './AdminServices';
import AdminServicesBulk from './AdminServicesBulk';
import CalendarView from './CalendarView';
import AdminSlotManagement from './AdminSlotManagement';
import AdminChatbot from './AdminChatbot';
import AdminPages from './AdminPages';
import AdminScripts from './AdminScripts';
import AdminMediaLibrary from './AdminMediaLibrary';
import AdminUsers from './AdminUsers';
import AdminBackups from './AdminBackups';
import AdminReviews from './AdminReviews';
import AdminRedirects from './AdminRedirects';
import AdminPartners from './AdminPartners';
import AdminCustomerCases from './AdminCustomerCases';
import AdminCustomers from './AdminCustomers';
import AdminVehicles from './AdminVehicles';
import AdminBookingsBulk from './AdminBookingsBulk';
import MechanicDashboard from './MechanicDashboard';
import UserManual from './UserManual';
import AdminFeedback from './AdminFeedback';
import AdminEmailCampaigns from './AdminEmailCampaigns';
import AdminCompanyInfo from './AdminCompanyInfo';
import AdminReports from './AdminReports';
import AdminThemeSettings from './AdminThemeSettings';
import AdminSchemaTest from './AdminSchemaTest';
import AdminNotificationSettings from './AdminNotificationSettings';
import { MediaHealthBanner } from './MediaHealthBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  
  // If user is mechanic, show simplified dashboard
  if (user?.role === 'mechanic') {
    return <MechanicDashboard />;
  }
  
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [activeView, setActiveView] = useState(
    user?.role === 'content_editor' ? 'pages' : 'overview'
  );

  useEffect(() => {
    fetchData(true);
    // Auto-refresh every 30 seconds (silent refresh)
    const interval = setInterval(() => fetchData(false), 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async (isInitial = false) => {
    try {
      const [statsRes, logsRes] = await Promise.all([
        axios.get(`${API}/logs/stats?hours=24`),
        axios.get(`${API}/logs?limit=50`)
      ]);
      setStats(statsRes.data);
      setLogs(logsRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      if (isInitial) {
        setInitialLoading(false);
      }
    }
  };

  if (initialLoading || !stats) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading dashboard...</div>
      </div>
    );
  }

  // Render the active content based on selected menu item
  const renderContent = () => {
    switch (activeView) {
      case 'overview':
        return <DashboardOverview stats={stats} logs={logs} onNavigate={setActiveView} />;
      case 'calendar':
        return <CalendarView />;
      case 'bookings':
        return <AdminBookings />;
      case 'bookings-bulk':
        return <AdminBookingsBulk />;
      case 'customers':
        return <AdminCustomers />;
      case 'vehicles':
        return <AdminVehicles />;
      case 'services':
        return <AdminServices />;
      case 'bulk':
        return <AdminServicesBulk />;
      case 'slots':
        return <AdminSlotManagement />;
      case 'notification-settings':
        return <AdminNotificationSettings />;
      case 'chatbot':
        return <AdminChatbot />;
      case 'media':
        return <AdminMediaLibrary />;
      case 'users':
        return <AdminUsers />;
      case 'backup':
        return <AdminBackups />;
      case 'reviews':
        return <AdminReviews />;
      case 'scripts':
        return <AdminScripts />;
      case 'redirects':
        return <AdminRedirects />;
      case 'partners':
        return <AdminPartners />;
      case 'cases':
        return <AdminCustomerCases />;
      case 'pages':
        return <AdminPages />;
      case 'cms':
        return <AdminCMS />;
      case 'help':
        return <UserManual />;
      case 'feedback':
        return <AdminFeedback />;
      case 'email-campaigns':
        return <AdminEmailCampaigns />;
      case 'company-info':
        return <AdminCompanyInfo />;
      case 'reports':
        return <AdminReports />;
      case 'theme-settings':
        return <AdminThemeSettings />;
      case 'schema-test':
        return <AdminSchemaTest />;
      default:
        return <DashboardOverview stats={stats} logs={logs} />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex">
      {/* Sidebar */}
      <AdminSidebar 
        activeTab={activeView} 
        setActiveTab={setActiveView} 
        onLogout={logout}
        user={user}
      />
      
      {/* Main Content Area */}
      <main className="flex-1 p-6 lg:p-8 overflow-auto">
        <div className="max-w-7xl mx-auto">
          {['master_admin', 'company_owner'].includes(user?.role) && (
            <MediaHealthBanner onOpenMedia={() => setActiveView('media')} />
          )}
          {/* Stats Grid - Always visible at top */}
          <div className={`grid grid-cols-1 md:grid-cols-2 ${user?.role === 'master_admin' ? 'lg:grid-cols-4' : 'lg:grid-cols-2'} gap-6 mb-8`}>
            {/* Total Logs - Master Admin Only */}
            {user?.role === 'master_admin' && (
              <Card className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 border-gray-700 backdrop-blur-sm">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-gray-300">Totale logger</CardTitle>
                  <Activity className="w-4 h-4 text-blue-400" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-white">{stats.total_logs}</div>
                  <p className="text-xs text-gray-500 mt-1">Siste 24 timer</p>
                </CardContent>
              </Card>
            )}

            {/* Errors - Master Admin Only */}
            {user?.role === 'master_admin' && (
              <Card className="bg-gradient-to-br from-red-900/30 to-red-800/30 border-red-700/50 backdrop-blur-sm">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-gray-300">Feil</CardTitle>
                  <AlertCircle className="w-4 h-4 text-red-400" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-white">{stats.errors_count}</div>
                  <p className="text-xs text-red-400 mt-1">
                    {stats.warnings_count} advarsler
                  </p>
                </CardContent>
              </Card>
            )}

            <Card className="bg-gradient-to-br from-green-900/30 to-green-800/30 border-green-700/50 backdrop-blur-sm">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-300">Sidevisninger</CardTitle>
                <Eye className="w-4 h-4 text-green-400" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-white">{stats.page_views}</div>
                <p className="text-xs text-green-400 mt-1">Unike besøk</p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-900/30 to-purple-800/30 border-purple-700/50 backdrop-blur-sm">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-300">Brukerhandlinger</CardTitle>
                <MousePointer className="w-4 h-4 text-purple-400" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-white">{stats.user_actions}</div>
                <p className="text-xs text-purple-400 mt-1">Interaksjoner</p>
              </CardContent>
            </Card>
          </div>

          {/* Dynamic Content */}
          {renderContent()}
        </div>
      </main>
    </div>
  );
};

// Dashboard Overview Component (extracted from tabs)
const DashboardOverview = ({ stats, logs, onNavigate }) => {
  const [themeSettings, setThemeSettings] = useState(null);

  useEffect(() => {
    const fetchTheme = async () => {
      try {
        const res = await axios.get(`${API}/theme-settings`);
        setThemeSettings(res.data);
      } catch (e) { /* ignore */ }
    };
    fetchTheme();
  }, []);

  const getLogoUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url.startsWith('/') ? '' : '/'}${url}`;
  };

  return (
    <div className="space-y-6">
      {/* Logo Preview Panel */}
      {themeSettings && (
        <Card className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 border-gray-700" data-testid="logo-preview-panel">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-white flex items-center space-x-2">
                <Image className="w-5 h-5 text-yellow-400" />
                <span>Logo-forhåndsvisning</span>
              </CardTitle>
              <button
                onClick={() => onNavigate?.('theme-settings')}
                className="text-xs text-yellow-400 hover:text-yellow-300 flex items-center gap-1 transition-colors"
                data-testid="logo-preview-edit-link"
              >
                <Settings className="w-3 h-3" />
                Rediger i Tema & Utseende
              </button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-lg border border-gray-700 bg-gray-900 p-4 flex flex-col items-center gap-2">
                <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
                  <Moon className="w-3 h-3" />
                  Mørk bakgrunn
                </div>
                {themeSettings.logos?.dark_mode ? (
                  <img
                    src={getLogoUrl(themeSettings.logos.dark_mode)}
                    alt="Logo mørk modus"
                    className="h-12 w-auto object-contain"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <span className="text-gray-600 text-sm">Ingen logo</span>
                )}
              </div>
              <div className="rounded-lg border border-gray-300 bg-white p-4 flex flex-col items-center gap-2">
                <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-1">
                  <Sun className="w-3 h-3" />
                  Lys bakgrunn
                </div>
                {themeSettings.logos?.light_mode ? (
                  <img
                    src={getLogoUrl(themeSettings.logos.light_mode)}
                    alt="Logo lys modus"
                    className="h-12 w-auto object-contain"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <span className="text-gray-400 text-sm">Ingen logo</span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Errors */}
        <Card className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center space-x-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              <span>Nylige feil</span>
            </CardTitle>
            <CardDescription className="text-gray-400">
              Siste feillogger
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.recent_errors?.length > 0 ? (
                stats.recent_errors.slice(0, 5).map((error, index) => (
                  <div
                    key={index}
                    className="bg-red-900/20 border border-red-700/30 rounded-lg p-3"
                  >
                    <p className="text-sm text-gray-300 mb-1">{error.message}</p>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>{error.page_url}</span>
                      <span>{new Date(error.timestamp).toLocaleTimeString('no-NO')}</span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-4">Ingen feil funnet</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Top Pages */}
        <Card className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center space-x-2">
              <TrendingUp className="w-5 h-5 text-green-400" />
              <span>Mest besøkte sider</span>
            </CardTitle>
            <CardDescription className="text-gray-400">
              Sider med flest besøk
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.top_pages?.length > 0 ? (
                stats.top_pages.slice(0, 5).map((page, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between bg-gray-800/50 rounded-lg p-3 border border-gray-700"
                  >
                    <span className="text-sm text-gray-300 truncate flex-1">
                      {page.page}
                    </span>
                    <Badge className="bg-green-600/20 text-green-400 border-green-600/50">
                      {page.views} visninger
                    </Badge>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-4">Ingen sidevisninger ennå</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Hourly Activity Chart */}
      <Card className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center space-x-2">
            <Clock className="w-5 h-5 text-blue-400" />
            <span>Aktivitet per time</span>
          </CardTitle>
          <CardDescription className="text-gray-400">
            Aktivitetsfordeling over tid
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {stats.hourly_activity?.length > 0 ? (
              stats.hourly_activity.map((hour, index) => {
                const maxCount = Math.max(...stats.hourly_activity.map(h => h.count));
                const width = maxCount > 0 ? (hour.count / maxCount) * 100 : 0;
                return (
                  <div key={index} className="flex items-center space-x-3">
                    <span className="text-xs text-gray-500 w-32">{hour.hour}</span>
                    <div className="flex-1 bg-gray-800 rounded-full h-6 relative overflow-hidden">
                      <div
                        className="bg-gradient-to-r from-red-600 to-yellow-400 h-full rounded-full flex items-center justify-end px-2 transition-all duration-500"
                        style={{ width: `${width}%` }}
                      >
                        <span className="text-xs font-semibold text-white">
                          {hour.count}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-gray-500 text-center py-4">Ingen aktivitetsdata</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Error Logs Section */}
      <Card className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white">Feillogger</CardTitle>
          <CardDescription className="text-gray-400">
            Alle feiloppføringer
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {logs.filter(log => log.level === 'error').length > 0 ? (
              logs.filter(log => log.level === 'error').map((log, index) => (
                <div
                  key={index}
                  className="bg-red-900/20 border border-red-700/30 rounded-lg p-4"
                >
                  <div className="flex items-start justify-between mb-2">
                    <Badge className="bg-red-600 text-white">
                      {log.category}
                    </Badge>
                    <span className="text-xs text-gray-500">
                      {new Date(log.timestamp).toLocaleString('no-NO')}
                    </span>
                  </div>
                  <p className="text-sm text-gray-300">{log.message}</p>
                  {log.page_url && (
                    <p className="text-xs text-gray-500 mt-2">{log.page_url}</p>
                  )}
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-4">Ingen feillogger</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
